import { useState } from "react";
import Navigation from "../components/Navigation";
import Footer from "../components/Footer";
import { Mail, Phone, MapPin, Instagram, Facebook, Send, Check } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: ""
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.message) {
      toast.error("Please fill in all required fields");
      return;
    }

    setSubmitting(true);

    try {
      await axios.post(`${API}/contact`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        message: formData.message
      });
      setSubmitted(true);
      toast.success("Message sent successfully!");
    } catch (error) {
      console.error("Contact form error:", error);
      toast.error("There was an error sending your message. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="contact-page">
      <Navigation />

      {/* Hero */}
      <section className="pt-32 pb-16 bg-[#050505]">
        <div className="container-custom text-center">
          <p className="label-text text-yellow-400 mb-4">Get in Touch</p>
          <h1 className="heading-xl text-white mb-6">Contact Us</h1>
          <p className="text-body max-w-2xl mx-auto">
            Have questions about our services or want to discuss your event? 
            We'd love to hear from you.
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="pb-24 bg-[#050505]" data-testid="contact-section">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Info */}
            <div>
              <h2 className="heading-lg text-white mb-8">Let's Talk</h2>
              
              <div className="space-y-6 mb-12">
                <a 
                  href="mailto:noah@charlesworth.club"
                  className="flex items-center gap-4 p-4 surface-card hover:border-yellow-400/50 transition-all group"
                  data-testid="contact-email"
                >
                  <div className="w-12 h-12 bg-yellow-400/10 flex items-center justify-center text-yellow-400">
                    <Mail size={24} />
                  </div>
                  <div>
                    <p className="label-text mb-1">Email</p>
                    <p className="text-white group-hover:text-yellow-400 transition-colors">
                      noah@charlesworth.club
                    </p>
                  </div>
                </a>

                <div 
                  className="flex items-center gap-4 p-4 surface-card"
                  data-testid="contact-location"
                >
                  <div className="w-12 h-12 bg-yellow-400/10 flex items-center justify-center text-yellow-400">
                    <MapPin size={24} />
                  </div>
                  <div>
                    <p className="label-text mb-1">Location</p>
                    <p className="text-white">Surrey & Surrounding Areas</p>
                  </div>
                </div>
              </div>

              {/* Social Links */}
              <div>
                <p className="label-text mb-4">Follow Us</p>
                <div className="flex gap-4">
                  <a
                    href="https://www.instagram.com/charlesworthclub/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 surface-card flex items-center justify-center text-white hover:text-yellow-400 hover:border-yellow-400 transition-all"
                    data-testid="social-instagram"
                  >
                    <Instagram size={24} />
                  </a>
                  <a
                    href="https://www.facebook.com/charlesworthclub"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 surface-card flex items-center justify-center text-white hover:text-yellow-400 hover:border-yellow-400 transition-all"
                    data-testid="social-facebook"
                  >
                    <Facebook size={24} />
                  </a>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="surface-card p-8" data-testid="contact-form-container">
              {submitted ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 mx-auto mb-6 bg-green-500/20 flex items-center justify-center">
                    <Check className="w-8 h-8 text-green-400" />
                  </div>
                  <h3 className="heading-md text-white mb-4">Message Sent!</h3>
                  <p className="text-body">
                    Thank you for getting in touch. We'll get back to you as soon as possible.
                  </p>
                </div>
              ) : (
                <>
                  <h3 className="heading-md text-white mb-6">Send a Message</h3>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                      <label className="label-text block mb-2">Name *</label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        className="input-dark"
                        placeholder="Your name"
                        data-testid="contact-input-name"
                      />
                    </div>

                    <div>
                      <label className="label-text block mb-2">Email *</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        className="input-dark"
                        placeholder="your@email.com"
                        data-testid="contact-input-email"
                      />
                    </div>

                    <div>
                      <label className="label-text block mb-2">Phone (Optional)</label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="input-dark"
                        placeholder="+44 7123 456789"
                        data-testid="contact-input-phone"
                      />
                    </div>

                    <div>
                      <label className="label-text block mb-2">Message *</label>
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        className="input-dark min-h-[150px]"
                        placeholder="Tell us about your event..."
                        data-testid="contact-input-message"
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={submitting}
                      className="btn-primary w-full flex items-center justify-center gap-2"
                      data-testid="contact-submit"
                    >
                      {submitting ? (
                        <>
                          <div className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          Send Message
                          <Send size={18} />
                        </>
                      )}
                    </button>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;
